import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { experienceService } from '../../../../services/experienceService';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const ExperienceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    company: '',
    logo: '',
    logoAlt: '',
    role: '',
    period: '',
    duration: '',
    location: '',
    type: '',
    achievements: '',
    displayOrder: 0
  });

  useEffect(() => {
    if (isEdit) {
      fetchExperience();
    }
  }, [id]);

  const fetchExperience = async () => {
    try {
      setLoading(true);
      const data = await experienceService?.getExperienceById(id);
      setFormData({
        company: data?.company || '',
        logo: data?.logo || '',
        logoAlt: data?.logoAlt || '',
        role: data?.role || '',
        period: data?.period || '',
        duration: data?.duration || '',
        location: data?.location || '',
        type: data?.type || '',
        achievements: data?.achievements?.join('\n') || '',
        displayOrder: data?.displayOrder || 0
      });
    } catch (err) {
      console.error('Error fetching experience:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        achievements: formData?.achievements?.split('\n')?.map(a => a?.trim())?.filter(Boolean),
        displayOrder: parseInt(formData?.displayOrder) || 0
      };

      if (isEdit) {
        await experienceService?.updateExperience(id, submitData);
      } else {
        await experienceService?.createExperience(submitData);
      }
      navigate('/admin/dashboard/experience');
    } catch (err) {
      console.error('Error saving experience:', err);
      setError(err?.message || 'Failed to save experience');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Edit Experience' : 'New Experience'} - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard/experience">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">
                  {isEdit ? 'Edit Experience' : 'New Experience'}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                  Company *
                </label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  placeholder="Enter company name"
                  value={formData?.company}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
                  Role *
                </label>
                <Input
                  id="role"
                  name="role"
                  type="text"
                  placeholder="Enter job role"
                  value={formData?.role}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="period" className="block text-sm font-medium text-foreground mb-2">
                  Period *
                </label>
                <Input
                  id="period"
                  name="period"
                  type="text"
                  placeholder="e.g., 2020 - 2023"
                  value={formData?.period}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-foreground mb-2">
                  Duration *
                </label>
                <Input
                  id="duration"
                  name="duration"
                  type="text"
                  placeholder="e.g., 3 years"
                  value={formData?.duration}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
                  Location *
                </label>
                <Input
                  id="location"
                  name="location"
                  type="text"
                  placeholder="e.g., Remote"
                  value={formData?.location}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-foreground mb-2">
                Type *
              </label>
              <Input
                id="type"
                name="type"
                type="text"
                placeholder="e.g., Full-time, Contract, Freelance"
                value={formData?.type}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="logo" className="block text-sm font-medium text-foreground mb-2">
                Logo URL
              </label>
              <Input
                id="logo"
                name="logo"
                type="url"
                placeholder="https://example.com/logo.jpg"
                value={formData?.logo}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="logoAlt" className="block text-sm font-medium text-foreground mb-2">
                Logo Alt Text
              </label>
              <Input
                id="logoAlt"
                name="logoAlt"
                type="text"
                placeholder="Describe the logo"
                value={formData?.logoAlt}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="achievements" className="block text-sm font-medium text-foreground mb-2">
                Achievements (one per line)
              </label>
              <textarea
                id="achievements"
                name="achievements"
                rows={6}
                placeholder="Enter each achievement on a new line"
                value={formData?.achievements}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label htmlFor="displayOrder" className="block text-sm font-medium text-foreground mb-2">
                Display Order
              </label>
              <Input
                id="displayOrder"
                name="displayOrder"
                type="number"
                placeholder="0"
                value={formData?.displayOrder}
                onChange={handleChange}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower numbers appear first
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                iconName={loading ? 'Loader2' : 'Save'}
                iconPosition="left"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Experience' : 'Create Experience'}
              </Button>
              <Link to="/admin/dashboard/experience">
                <Button type="button" variant="outline" disabled={loading}>
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default ExperienceForm;